import { useEffect, useLayoutEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "../core/i18n";
import useLanguageStore from "../stores/languageStore";
import { getLanguageFromPath, getLocalizedPath } from "../utils/urlUtils";

interface LanguageProviderProps {
  children: React.ReactNode;
}

interface LanguageRouteHandlerProps {
  children: React.ReactNode;
}

// Language metadata component using React Helmet
function LanguageMetadata({ language }: { language: SupportedLanguage }) {
  const languageInfo = SUPPORTED_LANGUAGES[language];

  // Locale mapping for OpenGraph
  const localeMap: Record<SupportedLanguage, string> = {
    en: "en_US",
    es: "es_ES",
    fr: "fr_FR",
    de: "de_DE",
    it: "it_IT",
    pt: "pt_BR",
    ru: "ru_RU",
    ja: "ja_JP",
    hi: "hi_IN",
    pl: "pl_PL",
    zh: "zh_CN",
    id: "id_ID",
    tr: "tr_TR",
    nl: "nl_NL",
    vi: "vi_VN",
    ko: "ko_KR",
  };

  return (
    <Helmet>
      <html lang={language} />
      <meta name="language" content={languageInfo.name} />
      <meta property="og:locale" content={localeMap[language] || "en_US"} />
    </Helmet>
  );
}

// Global language provider for the entire app
export function LanguageProvider({ children }: LanguageProviderProps) {
  const { i18n } = useTranslation();
  const { currentLanguage, setLanguage } = useLanguageStore();

  // Sync the language FROM the URL only on initial load and on real browser
  // back/forward navigation (popstate). We intentionally do NOT depend on
  // `currentLanguage` here: the app uses a data router whose async loaders
  // defer the URL update during client navigation, so re-running this on every
  // language change would read a stale `window.location` and revert a
  // user-initiated language switch back to the previous language.
  useLayoutEffect(() => {
    const syncLanguageFromUrl = () => {
      const urlLanguage = getLanguageFromPath();
      const pathSegment = window.location.pathname.split("/").filter(Boolean)[0];

      if (pathSegment && pathSegment in SUPPORTED_LANGUAGES) {
        const { currentLanguage: stored } = useLanguageStore.getState();
        if (urlLanguage !== stored) {
          setLanguage(urlLanguage);
        }
        if (i18n.language !== urlLanguage) {
          i18n.changeLanguage(urlLanguage);
        }
      }
    };

    syncLanguageFromUrl();
    window.addEventListener("popstate", syncLanguageFromUrl);
    return () => window.removeEventListener("popstate", syncLanguageFromUrl);
  }, [i18n, setLanguage]);

  useEffect(() => {
    // Sync i18n language with store on mount
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }

    // Listen for i18n language changes and update store
    const handleLanguageChanged = (lng: string) => {
      if (lng !== currentLanguage && lng in SUPPORTED_LANGUAGES) {
        setLanguage(lng as SupportedLanguage);
      }
    };

    i18n.on("languageChanged", handleLanguageChanged);

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, [i18n, currentLanguage, setLanguage]);

  return (
    <>
      <LanguageMetadata language={currentLanguage} />
      {children}
    </>
  );
}

// Route-specific language handler
export function LanguageRouteHandler({ children }: LanguageRouteHandlerProps) {
  const { lang } = useParams<{ lang?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { setLanguage } = useLanguageStore();

  useEffect(() => {
    // Use getState() to get the current language synchronously
    // This ensures we always read the latest value, even if the component
    // hasn't re-rendered yet after setLanguage is called
    const currentLanguage = useLanguageStore.getState().currentLanguage;

    // Check if language from URL is valid
    if (lang && lang in SUPPORTED_LANGUAGES) {
      const langCode = lang as SupportedLanguage;

      // If language is English, redirect to remove "en/" prefix
      if (langCode === "en") {
        const pathWithoutLang = location.pathname.replace(/^\/en/, "") || "/";
        const newPath = pathWithoutLang + location.search;
        if (location.pathname !== newPath) {
          navigate(newPath, { replace: true });
          return;
        }
      }

      // Only sync store/i18n when the URL actually carries this language prefix.
      // During a route transition the effect can fire with stale params (e.g.
      // lang="hi") while location.pathname has already moved to the English
      // path – syncing in that state would revert the user's language choice.
      const urlHasLangPrefix =
        location.pathname.startsWith(`/${langCode}/`) ||
        location.pathname === `/${langCode}`;

      // Update language store if different
      if (langCode !== currentLanguage && urlHasLangPrefix) {
        setLanguage(langCode);
      }

      // Update i18n language if different
      if (i18n.language !== langCode && urlHasLangPrefix) {
        i18n.changeLanguage(langCode);
      }
    } else if (lang && !(lang in SUPPORTED_LANGUAGES)) {
      // Invalid language code, redirect to version without language prefix (English default)
      const newPath = location.pathname.replace(`/${lang}`, "") || "/";
      navigate(newPath + location.search, { replace: true });
    } else if (!lang) {
      // No language in URL
      if (currentLanguage === "en") {
        // For English, we're already on the correct path (no prefix needed)
        // Just ensure language store and i18n are synced
        if (i18n.language !== "en") {
          i18n.changeLanguage("en");
        }
      } else {
        // For non-English languages, redirect to language-prefixed URL
        const pathWithLang =
          getLocalizedPath(location.pathname, currentLanguage) +
          location.search;
        if (location.pathname + location.search !== pathWithLang) {
          navigate(pathWithLang, { replace: true });
        }
      }
    }
  }, [lang, setLanguage, i18n, navigate, location]);

  // Only render children if we have a valid language or are in the process of redirecting
  if (lang && !(lang in SUPPORTED_LANGUAGES)) {
    return null; // Don't render while redirecting from invalid language
  }

  return <>{children}</>;
}
